import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

import { Chart } from "highcharts";
import { first } from "rxjs/operators";
import { addHours } from "ts-date";

import { dffHighchartsRemoveAllSeries, dffHighchartsSetSeries, dffShowHide } from "@dffedb/highcharts";
import { DffSimpelDialog, ToastService } from "@dffedb/ui";
import { addDays, addMonths, addYears, DffError, formatDate, maxDate, parseDate, resetDate, resetMonth } from "@dffedb/util";
import { LogService } from "@e-forsyning/common/log";
import { RedirectService } from "@e-forsyning/common/router";

import { MitForbrugSendFejlData } from "../mit-forbrug-send-fejl-dialog/mit-forbrug-send-fejl-data.model";
import { Forbrug, ForbrugsLinje, Periode, Taellevaerk } from "./forbrug.model";
import { Tile } from "./mit-forbrug-indstillinger.model";
import { ForbrugParams, MitForbrugViewModel } from "./mit-forbrug.view-model";
import { NyMitForbrugService } from "./ny-mit-forbrug.service";
import { SendFejlforbrugMailService } from "./send-fejlforbrug-mail.service";

@Injectable({ providedIn: "root" })
export class NyForbrugUtils {
    constructor(
        private readonly domSanitizer: DomSanitizer,
        private readonly redirectService: RedirectService,
        private readonly simpelDialog: DffSimpelDialog,
        private readonly mailService: SendFejlforbrugMailService,
        private readonly forbrugService: NyMitForbrugService,
        private readonly logger: LogService,
        private readonly toastService: ToastService
    ) {}

    public getMonthList(): any {
        return [
            { id: 1, value: "jan", year: 0 },
            { id: 2, value: "feb", year: 0 },
            { id: 3, value: "mar", year: 0 },
            { id: 4, value: "apr", year: 0 },
            { id: 5, value: "maj", year: 0 },
            { id: 6, value: "jun", year: 0 },
            { id: 7, value: "jul", year: 0 },
            { id: 8, value: "aug", year: 0 },
            { id: 9, value: "sep", year: 0 },
            { id: 10, value: "okt", year: 0 },
            { id: 11, value: "nov", year: 0 },
            { id: 12, value: "dec", year: 0 }
        ];
    }

    public async downloadAflaesFile(vm: MitForbrugViewModel) {
        // TODO AJH this.usSpinnerService.spin("spinner-1");
        try {
            return this.forbrugService.getAflaesFile(vm).pipe(first()).toPromise();
            // TODO AJH this.usSpinnerService.stop("spinner-1");
        } catch (error) {
            this.aError(error);
            throw error;
        }
    }

    public async sendFejlData(data: MitForbrugSendFejlData, aar: number, maaned: number, visningstype: number): Promise<void> {
        const maildata = {
            ...data,
            aar,
            maaned,
            visningstype: this.visningstypeToString(visningstype)
        };
        await this.mailService.sendMail(maildata);
    }

    public drillDownTemperatureChart(vm: MitForbrugViewModel, date: any): void {
        const synligeFaner = vm.umbracoMitForbrug.temperaturgraf.faner;
        if (vm.tempSelectedInterval === 3 && synligeFaner.maaned) {
            vm.tempSelectedYear = new Date(date).getUTCFullYear();
            vm.tempSelectedInterval = 1;
        } else if (vm.tempSelectedInterval === 1 && synligeFaner.dag) {
            vm.tempSelectedMonth.id = new Date(date).getUTCMonth() + 1;
            vm.tempSelectedMonth.year = new Date(date).getUTCFullYear();
            vm.tempSelectedInterval = 0;
        } else {
            return;
        }
        this.resetTempChart(vm);
    }

    public tileClick(vm: MitForbrugViewModel, tile: Tile): void {
        if (tile.type === "eksternlink") {
            window.open(tile.link, "_blank");
        } else if (tile.link) {
            this.redirectService.redirect(tile.link);
        } else if (tile.type === "Vejret") {
            const vejret = vm.umbracoMitForbrug.vejret;
            if (vejret.titel || vejret.tekst) {
                this.simpelDialog.openOk(vejret.titel, this.domSanitizer.bypassSecurityTrustHtml(vejret.tekst), {
                    maxWidth: 600
                });
            }
        } else if (tile.type === "Temperatur") {
            this.scrollIntoView("temperatur-chart-panel");
            const panel = document.getElementById("temperatur-chart-panel");
            if (panel) {
                const firstFocusableElement = panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
                if (firstFocusableElement) {
                    firstFocusableElement.focus();
                }
            }
        } else if (tile.type === "Budget") {
            this.scrollIntoView("forbrug-chart-panel");
            const panel = document.getElementById("forbrug-chart-panel");
            if (panel) {
                const firstFocusableElement = panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
                if (firstFocusableElement) {
                    firstFocusableElement.focus();
                }
            }
        } else if (tile.type === "Mit Forbrug") {
            this.scrollIntoView("forbrug-chart-panel");
            const panel = document.getElementById("forbrug-chart-panel");
            if (panel) {
                const firstFocusableElement = panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
                if (firstFocusableElement) {
                    firstFocusableElement.focus();
                }
            }
        }
    }

    public getDigitInfo(taellevaerkenhed: string): string {
        taellevaerkenhed = (taellevaerkenhed || "").trim().toLowerCase();
        switch (taellevaerkenhed) {
            case "m³":
            case "mwh":
                return "1.3-3";
            case "kwh":
                return "1.0-0";
            default:
                return "1.2-2";
        }
    }

    public async getAarsmaerke(vm: MitForbrugViewModel): Promise<void> {
        // TODO AJH this.usSpinnerService.spin("spinner-1");
        try {
            const result = await this.forbrugService.getAktuelAarsmaerke().pipe(first()).toPromise();

            vm.aarsmaerke = result.aarsmaerke;
            vm.selectedYear = vm.aarsmaerke;
            vm.aarsmaerkeStart = result.aarsmaerke_start;
            vm.aarsmaerkeSlut = result.aarsmaerke_slut;

            // måske ikke nødvendig, er lavet for at kunne se skæve år this.vm.aarsmaerker = result.aarsmaerker;
            vm.tempSelectedYear = vm.selectedYear;
            vm.forbrugsParams.AarsMaerke = vm.aarsmaerke;
            vm.yearList = this.getYearList(vm);
        } catch (error) {
            this.aError(error);
            throw error;
        }
    }

    public async reloadTempChart(vm: MitForbrugViewModel): Promise<void> {
        if (vm.tempSelectedInterval === 3) {
            return await this.drawTemperaturYear(vm);
        }
        if (vm.tempSelectedInterval === 1) {
            return await this.drawTemperaturMonth(vm);
        }
        if (vm.tempSelectedInterval === 0) {
            return await this.drawTemperaturDay(vm);
        }
        throw new DffError(`Ugyldigt visning på temperaturgrafen: ${vm.tempSelectedInterval}`);
    }

    public async reloadForbrugChart(vm: MitForbrugViewModel): Promise<string[]> {
        if (vm.selectedInterval === 3) {
            return await this.drawForbrugYear(vm);
        }
        if (vm.selectedInterval === 1) {
            return await this.drawForbrugMonth(vm);
        }
        if (vm.selectedInterval === 0) {
            return await this.drawForbrugDay(vm);
        }
        throw new DffError(`Ugyldigt visning på forbrugsgrafen: ${vm.selectedInterval}`);
    }

    public getFirstShownYear(vm: MitForbrugViewModel): number {
        return this.getForbrugMinYear(vm);
    }

    // TODO NIM: Vi bruger ikke selve listen til noget. Kun den beregnede måned. Koden kunne nok gøres noget enklere.
    // TODO NIM: Husk at tage højde for årsmærke som ikke starter 1. januar.
    public createMonthList(vm: MitForbrugViewModel, aarsmaerkeStart: string, year: number): any {
        const startMonth = this.getStartMonth(vm, aarsmaerkeStart);
        const startdateArray = aarsmaerkeStart.split("-");
        const resultMonthList = [];
        const month = parseInt(startdateArray[1], 10);
        const d = new Date();
        const thisMonth = d.getMonth() + 1;
        let a = 0;
        let m: any;
        let selectedMonth = "";
        if (year === 0) {
            year = parseInt(startdateArray[2], 10);
        }
        let dontShowFirstMonths = false;
        if (startMonth < month) {
            dontShowFirstMonths = true;
        }
        for (let i = month - 1; i <= vm.monthList.length - 1; i++) {
            vm.monthList[i].year = year;
            resultMonthList[a] = vm.monthList[i];

            m = { ...vm.monthList[i] };
            m.dontShow = startMonth > vm.monthList[i] || dontShowFirstMonths;
            resultMonthList[a] = m;
            if (resultMonthList[a].id === thisMonth) {
                selectedMonth = resultMonthList[a];
            }
            a++;
        }
        if (month > 1) {
            for (let i = 0; i < month - 1; i++) {
                vm.monthList[i].year = year + 1;
                m = { ...vm.monthList[i] };
                m.value += " " + (year + 1);
                m.dontShow = false;
                resultMonthList[a] = m;
                if (resultMonthList[a].id === thisMonth) {
                    selectedMonth = resultMonthList[a];
                }
                a++;
            }
            if (selectedMonth === "") {
                selectedMonth = resultMonthList[a - 1];
            }
        }
        return {
            monthList: resultMonthList,
            selectedMonth
        };
    }

    public getFirstShownTemperatureYear(vm: MitForbrugViewModel): number {
        return this.getTemperaturMinYear(vm);
    }

    private scrollIntoView(elementId: string): void {
        setTimeout(() => {
            const element = document.getElementById(elementId);
            if (element) {
                element.scrollIntoView();
            }
        }, 350);
    }

    private visningstypeToString(visningstype: number): string {
        return visningstype === 0
            ? "Dagsvisning"
            : visningstype === 1
              ? "Månedsvisning"
              : visningstype === 2
                ? "Sammenlign"
                : visningstype === 3
                  ? "Årsvisning"
                  : `Ukendt visningstype: ${visningstype}`;
    }

    private async drawTemperaturDay(vm: MitForbrugViewModel): Promise<void> {
        const aarsmaerkeStart = parseDate(vm.aarsmaerkeStart, "DD-MM-YYYY");
        const aarsmaerkeSlut = parseDate(vm.aarsmaerkeSlut, "DD-MM-YYYY");

        const getData = async (aarsmaerke: number): Promise<Forbrug> =>
            await this.httpGetForbrug({
                /* eslint-disable @typescript-eslint/naming-convention */
                AarsMaerke: aarsmaerke,
                Aflaesningsfilter: "afUfiltreret",
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: aarsmaerke === vm.aarsmaerke ? "2" : "0",
                ForbrugsAfgraensning_FraDato: formatDate(new Date(aarsmaerke, aarsmaerkeStart.getMonth(), aarsmaerkeStart.getDate()), "DD-MM-YYYY"),
                ForbrugsAfgraensning_TilDato: formatDate(new Date(aarsmaerke + 1, aarsmaerkeSlut.getMonth(), aarsmaerkeSlut.getDate()), "DD-MM-YYYY"),
                MedForventetForbrug: aarsmaerke === vm.aarsmaerke,
                AflaesningsUdjaevning: false
                /* eslint-enable @typescript-eslint/naming-convention */
            });

        vm.tempChart.hideNoData();
        vm.tempChart.showLoading();
        try {
            const year = vm.tempSelectedMonth.year;
            const month = vm.tempSelectedMonth.id - 1;
            const aarsmaerke = this.getAarsmaerkeFraDato(vm, `01-${month + 1}-${year}`);

            // Dataene findes i nogle tilfælde i foregående årsmærke, så vi henter både for dette og forrige år.
            const [resultLastYear, resultThisYear] = await Promise.all([getData(aarsmaerke - 1), getData(aarsmaerke)]);
            const forbrugsLinjer = [...resultLastYear.forbrugsLinjer, ...resultThisYear.forbrugsLinjer];

            const curMonth = new Date(year, month, 1).getTime();
            const minMonth = resetMonth(this.getTemperaturMinDate(vm)).getTime();
            const maxMonth = resetMonth(this.getTemperaturMaxDate()).getTime();
            this.ShowTempNextPrevButtons(vm, curMonth, minMonth, maxMonth);

            const options = this.getTempChartOptions(resultThisYear, vm.tvIndexNavn, {
                chartTitle: formatDate(new Date(year, month, 1), "MMMM YYYY"),
                xMin: new Date(year, month, 1).getTime(),
                xMax: addMonths(new Date(year, month, 1), 1).getTime()
            });
            this.applyChartOptions(vm.tempChart, options, vm.temperaturPanelOpenState);

            const data = this.mapTemperaturLinjer(
                forbrugsLinjer,
                (l) => l,
                (l) => addHours(resetDate(l.periode.slut), 12)
            );

            this.drawTemperaturData(vm, data);
        } catch (error) {
            this.aErrorChart(error, vm.tempChart);
            throw error;
        } finally {
            vm.tempChart.hideLoading();
            if (!vm.tempChart.hasData()) {
                vm.tempChart.showNoData("Der er desværre ingen data at vise i denne periode");
            }
        }
    }

    private async drawTemperaturMonth(vm: MitForbrugViewModel): Promise<void> {
        const getData = async (aarsmaerke: number): Promise<Forbrug> =>
            await this.httpGetForbrug({
                /* eslint-disable @typescript-eslint/naming-convention */
                AarsMaerke: aarsmaerke,
                Aflaesningsfilter: "afMaanedsvis",
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: aarsmaerke === vm.aarsmaerke ? "2" : "0",
                MedForventetForbrug: aarsmaerke === vm.aarsmaerke,
                AflaesningsUdjaevning: true
                // ForbrugsAfgraensning_MedtagMellemliggendeMellemaflas: false,
                // Optioner: "foBestemtBeboer, foSkabDetaljer, foMedtagWebAflaes"
                /* eslint-enable @typescript-eslint/naming-convention */
            });

        vm.tempChart.hideNoData();
        vm.tempChart.showLoading();
        try {
            const year = vm.tempSelectedYear;
            const minYear = this.getTemperaturMinYear(vm);
            const maxYear = vm.aarsmaerke;

            const result = await getData(year);
            this.ShowTempNextPrevButtons(vm, year, minYear, maxYear);

            const chartTitle = this.getYearTitle(result.aar.start, result.aar.slut);
            const options = this.getTempChartOptions(result, vm.tvIndexNavn, {
                chartTitle,
                xMin: result.aar.start.getTime(),
                xMax: resetMonth(addMonths(result.aar.slut, 1)).getTime()
            });
            this.applyChartOptions(vm.tempChart, options, vm.temperaturPanelOpenState);

            const data = this.mapTemperaturLinjer(
                result.forbrugsLinjer,
                (l) => l,
                (l) => addDays(resetMonth(l.periode.slut), 15)
            );

            this.drawTemperaturData(vm, data);
        } catch (error) {
            this.aErrorChart(error, vm.tempChart);
            throw error;
        } finally {
            vm.tempChart.hideLoading();
            if (!vm.tempChart.hasData()) {
                vm.tempChart.showNoData("Der er desværre ingen data at vise i denne periode");
            }
        }
    }
    private async drawTemperaturYear(vm: MitForbrugViewModel): Promise<void> {
        const getData = async (aarsmaerke: number): Promise<Forbrug> =>
            await this.httpGetForbrug({
                /* eslint-disable @typescript-eslint/naming-convention */
                AarsMaerke: aarsmaerke,
                Aflaesningsfilter: "afMaanedsvis",
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: aarsmaerke === vm.aarsmaerke ? "2" : "0",
                MedForventetForbrug: false,
                AflaesningsUdjaevning: false,
                ForbrugsAfgraensning_MedtagMellemliggendeMellemaflas: false,
                Optioner: "foBestemtBeboer, foSkabDetaljer, foMedtagWebAflaes"
                /* eslint-enable @typescript-eslint/naming-convention */
            });

        vm.tempChart.hideNoData();
        vm.tempChart.showLoading();
        try {
            const year = vm.tempSelectedYear;
            const minYear = this.getTemperaturMinYear(vm);
            const maxYear = vm.aarsmaerke;

            const years = [year, year - 1, year - 2, year - 3, year - 4, year - 5].filter((y) => y >= minYear);
            const results = await Promise.all(years.map((y) => getData(y)));
            this.ShowTempNextPrevButtons(vm, year, minYear + 4, maxYear);

            const options = this.getTempChartOptions(results[0], vm.tvIndexNavn, {
                chartTitle: `${year - 4} - ${year}`,
                xMin: Date.UTC(year - 4, 0, 1),
                xMax: Date.UTC(year + 1, 0, 1)
            });
            this.applyChartOptions(vm.tempChart, options, vm.temperaturPanelOpenState);

            const data = this.mapTemperaturLinjer(
                results,
                (r) => r.ialtLinje,
                (r) => new Date((r.aar.start.getTime() + r.aar.slut.getTime()) / 2)
            );

            this.drawTemperaturData(vm, data);
        } catch (error) {
            this.aErrorChart(error, vm.tempChart);
            throw error;
        } finally {
            vm.tempChart.hideLoading();
            if (!vm.tempChart.hasData()) {
                vm.tempChart.showNoData("Der er desværre ingen data at vise i denne periode");
            }
        }
    }

    private drawTemperaturData(vm: MitForbrugViewModel, data: TemperaturChartPoints): void {
        const synlighed = vm.umbracoMitForbrug.temperaturgraf.synligeData;

        if (synlighed.fremloeb) {
            dffHighchartsSetSeries(
                vm.tempChart,
                vm.tempOptions.series.find((o) => o.id === "fremlob")
            ).setData(data.fremloeb);
        }

        if (synlighed.retur) {
            dffHighchartsSetSeries(
                vm.tempChart,
                vm.tempOptions.series.find((o) => o.id === "retur")
            ).setData(data.retur);
        }

        if (synlighed.afkoeling) {
            dffHighchartsSetSeries(
                vm.tempChart,
                vm.tempOptions.series.find((o) => o.id === "afkoel")
            ).setData(data.afkoeling);
        }

        if (synlighed.neutral) {
            dffHighchartsSetSeries(
                vm.tempChart,
                vm.tempOptions.series.find((o) => o.id === "neutral")
            ).setData(data.neutral);
        }

        if (synlighed.forventetRetur) {
            dffHighchartsSetSeries(
                vm.tempChart,
                vm.tempOptions.series.find((o) => o.id === "forventetRetur")
            ).setData(data.forventetRetur);
        }
    }

    private async drawForbrugYear(vm: MitForbrugViewModel): Promise<string[]> {
        const getData = async (aarsmaerke: number): Promise<Forbrug> =>
            await this.httpGetForbrug({
                /* eslint-disable @typescript-eslint/naming-convention */
                AarsMaerke: aarsmaerke,
                Aflaesningsfilter: "afMaanedsvis",
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: aarsmaerke === vm.aarsmaerke ? "2" : "0",
                MedForventetForbrug: false,
                AflaesningsUdjaevning: false,
                ForbrugsAfgraensning_MedtagMellemliggendeMellemaflas: false,
                Optioner:
                    aarsmaerke === vm.aarsmaerke
                        ? "foBestemtBeboer, foSkabDetaljer, foMedtagWebAflaes"
                        : "foMedtagikkeMellem, foBestemtBeboer, foSkabDetaljer, foMedtagWebAflaes"
                /* eslint-enable @typescript-eslint/naming-convention */
            });

        vm.chart.hideNoData();
        vm.chart.showLoading();
        try {
            const year = vm.selectedYear;
            const minYear = this.getForbrugMinYear(vm);
            const maxYear = vm.aarsmaerke;

            const years = [year, year - 1, year - 2, year - 3, year - 4].filter((y) => y >= minYear);
            const results = await Promise.all(years.map((y) => getData(y)));

            // Rapid7 melder om en fejl hvis results er null - det kan tilsyneladende ikke genskabes.
            // Derfor logger vi ekstra.
            if (!results) {
                this.logger.logError("Year: " + year + ", MinYear: " + minYear + ", MaxYear: " + maxYear + ", Years: " + years.toString());
            }
            this.ShowForbrugNextPrevButtons(vm, year, minYear + 4, maxYear);

            dffHighchartsRemoveAllSeries(vm.chart);
            const options = this.getForbrugChartOptions(results[0], vm.tvIndexNavn, {
                chartTitle: `${year - 4} - ${year}`,
                xMin: Date.UTC(vm.selectedYear - 5, 10, 1),
                xMax: Date.UTC(vm.selectedYear, 3, 1)
            });

            this.applyChartOptions(vm.chart, options, vm.forbrugPanelOpenState);

            const forbrug: ForbrugChartPoint[] = results
                .map((r) =>
                    this.mapForbrugsLinje(
                        r.ialtLinje,
                        vm.tvIndexNavn,
                        () => new Date(r.aarsmaerke, 0, 1),
                        () => r.aar
                    )
                )
                .sort((a, b) => a.x - b.x);

            const series = dffHighchartsSetSeries(vm.chart, vm.maaltSerie);
            series.setData(forbrug);
            return this.forbrugArrayToTaellevaerker(results);
        } catch (error) {
            this.aErrorChart(error, vm.chart);
            throw error;
        } finally {
            vm.chart.hideLoading();
            if (!vm.chart.hasData()) {
                vm.chart.showNoData("Der er desværre ingen data at vise i denne periode");
            }
        }
    }

    private async drawForbrugMonth(vm: MitForbrugViewModel): Promise<string[]> {
        const getData = async (aarsmaerke: number): Promise<Forbrug> =>
            await this.httpGetForbrug({
                /* eslint-disable @typescript-eslint/naming-convention */
                AarsMaerke: aarsmaerke,
                Aflaesningsfilter: "afMaanedsvis",
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: aarsmaerke === vm.aarsmaerke ? "2" : "0",
                MedForventetForbrug: aarsmaerke === vm.aarsmaerke,
                AflaesningsUdjaevning: true
                /* eslint-enable @typescript-eslint/naming-convention */
            });

        vm.chart.hideNoData();
        vm.chart.showLoading();
        try {
            const year = vm.selectedYear;
            const minYear = this.getForbrugMinYear(vm);
            const maxYear = vm.aarsmaerke;

            const years = vm.sammenlign === 0 ? [year] : [year, year - 1, year - 2].filter((y) => y >= minYear);
            const results = await Promise.all(years.map((y) => getData(y)));

            this.ShowForbrugNextPrevButtons(vm, year, minYear, maxYear);
            dffHighchartsRemoveAllSeries(vm.chart);

            // Rapid7 melder om en fejl hvis results er null - det kan tilsyneladende ikke genskabes.
            // Derfor logger vi ekstra.
            if (!results) {
                this.logger.logError("Year: " + year + ", MinYear: " + minYear + ", MaxYear: " + maxYear + ", Years: " + years.toString());
            }

            const chartTitle = this.getYearTitle(results[0].aar.start, results[0].aar.slut);
            this.applyChartOptions(vm.chart, this.getForbrugChartOptions(results[0], vm.tvIndexNavn, { chartTitle }), vm.forbrugPanelOpenState);

            if (vm.sammenlign === 0) {
                // Forbrug - Målt
                const forbrug = this.mapForbrugsLinjer(results[0].forbrugsLinjer, vm.tvIndexNavn, (d) => resetMonth(d)).filter((d) => !d.beregnet);
                const series = dffHighchartsSetSeries(vm.chart, vm.maaltSerie);
                series.setData(forbrug);

                // Forbrug - Beregnet
                const forbrugBeregnet = this.mapForbrugsLinjer(results[0].forbrugsLinjer, vm.tvIndexNavn, (d) => resetMonth(d)).filter((d) => d.beregnet);
                if (forbrugBeregnet.length) {
                    const seriesForbrugBeregnet = dffHighchartsSetSeries(vm.chart, vm.beregnetSerie);
                    seriesForbrugBeregnet.setData(forbrugBeregnet);
                }

                // Forbrug - Forventet
                if (year === vm.aarsmaerke) {
                    const forventetForbrug = this.mapForventetForbrugsLinjer(results[0], vm.tvIndexNavn, (d) => resetMonth(d));
                    dffHighchartsSetSeries(vm.chart, vm.forventetSerie).setData(forventetForbrug);
                }
            } else {
                // Forbrug - Valgte år
                const forbrug = this.mapForbrugsLinjer(results[0].forbrugsLinjer, vm.tvIndexNavn, (d) => resetMonth(d));
                dffHighchartsSetSeries(vm.chart, { ...vm.maaltSerie, name: year }).setData(forbrug);

                // Sammenlign - 1 år tidligere
                if (results[1]) {
                    const sammenlignForbrug = this.mapForbrugsLinjer(results[1].forbrugsLinjer, vm.tvIndexNavn, (d) => addYears(resetMonth(d), 1));
                    dffHighchartsSetSeries(vm.chart, {
                        ...vm.sammenlignSerie,
                        name: year - 1
                    }).setData(sammenlignForbrug);
                }

                // Sammenlign - 2 år tidligere
                if (results[2]) {
                    const sammenlignForbrug2 = this.mapForbrugsLinjer(results[2].forbrugsLinjer, vm.tvIndexNavn, (d) => addYears(resetMonth(d), 2));
                    dffHighchartsSetSeries(vm.chart, {
                        ...vm.sammenlignSerie2,
                        name: year - 2
                    }).setData(sammenlignForbrug2);
                }
            }
            return this.forbrugArrayToTaellevaerker(results);
        } catch (error) {
            this.aErrorChart(error, vm.chart);
            throw error;
        } finally {
            vm.chart.hideLoading();
            if (!vm.chart.hasData()) {
                vm.chart.showNoData("Der er desværre ingen data at vise i denne periode");
            }
        }
    }

    private ShowForbrugNextPrevButtons(vm: MitForbrugViewModel, period: number, minPeeriod: number, maxPeriod: number) {
        vm.chart.prevButtonVisible = period > minPeeriod;
        vm.chart.nextButtonVisible = period < maxPeriod;
        dffShowHide(vm.chart.exportSVGElements[0], vm.forbrugPanelOpenState ? period > minPeeriod : false);
        dffShowHide(vm.chart.exportSVGElements[2], vm.forbrugPanelOpenState ? period < maxPeriod : false);
    }

    private ShowTempNextPrevButtons(vm: MitForbrugViewModel, period: number, minPeeriod: number, maxPeriod: number) {
        vm.tempChart.prevButtonVisible = period > minPeeriod;
        vm.tempChart.nextButtonVisible = period < maxPeriod;
        dffShowHide(vm.tempChart.exportSVGElements[0], vm.temperaturPanelOpenState ? period > minPeeriod : false);
        dffShowHide(vm.tempChart.exportSVGElements[2], vm.temperaturPanelOpenState ? period < maxPeriod : false);
    }

    private getYearTitle(start: Date, slut: Date): string {
        return start.getFullYear() === slut.getFullYear() ? start.getFullYear().toString() : `${start.getFullYear()} - ${slut.getFullYear()}`;
    }

    private async drawForbrugDay(vm: MitForbrugViewModel): Promise<string[]> {
        const aarsmaerkeStart = parseDate(vm.aarsmaerkeStart, "DD-MM-YYYY");
        const aarsmaerkeSlut = parseDate(vm.aarsmaerkeSlut, "DD-MM-YYYY");

        const getData = async (aarsmaerke: number): Promise<Forbrug> =>
            await this.httpGetForbrug({
                /* eslint-disable @typescript-eslint/naming-convention */
                AarsMaerke: aarsmaerke,
                Aflaesningsfilter: "afDagsvis",
                ForbrugsAfgraensning_FraDato: formatDate(new Date(aarsmaerke, aarsmaerkeStart.getMonth(), aarsmaerkeStart.getDate()), "DD-MM-YYYY"),
                ForbrugsAfgraensning_TilDato: formatDate(new Date(aarsmaerke + 1, aarsmaerkeSlut.getMonth(), aarsmaerkeSlut.getDate()), "DD-MM-YYYY"),
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: aarsmaerke >= vm.aarsmaerke ? "2" : "0",
                MedForventetForbrug: aarsmaerke === vm.aarsmaerke,
                AflaesningsUdjaevning: true
                /* eslint-enable @typescript-eslint/naming-convention */
            });

        vm.chart.hideNoData();
        vm.chart.showLoading();
        try {
            const year = vm.selectedMonth.year;
            const month = vm.selectedMonth.id - 1;
            const aarsmaerke = this.getAarsmaerkeFraDato(vm, `01-${month + 1}-${year}`);

            // Dataene findes i nogle tilfælde i foregående årsmærke, så vi henter både for dette og forrige år.
            const [resultLastYear, resultThisYear] = await Promise.all([getData(aarsmaerke - 1), getData(aarsmaerke)]);
            const forbrugsLinjer = [...resultLastYear.forbrugsLinjer, ...resultThisYear.forbrugsLinjer];

            // TODO: Kan vi oprette noget i stil med vm.chartOptions.minDate og maxDate?
            const curMonth = new Date(year, month, 1).getTime();
            const minMonth = resetMonth(this.getForbrugMinDate(vm)).getTime();
            const maxMonth = resetMonth(this.getForbrugMaxDate()).getTime();
            this.ShowForbrugNextPrevButtons(vm, curMonth, minMonth, maxMonth);

            dffHighchartsRemoveAllSeries(vm.chart);
            const options = this.getForbrugChartOptions(resultThisYear, vm.tvIndexNavn, {
                chartTitle: formatDate(new Date(year, month, 1), "MMMM YYYY"),
                xMin: new Date(year, month, 1).getTime(),
                xMax: addDays(addMonths(new Date(year, month, 1), 1), -1).getTime()
            });
            this.applyChartOptions(vm.chart, options, vm.forbrugPanelOpenState);

            // Forbrug - Målt
            const forbrug = this.mapForbrugsLinjer(forbrugsLinjer, vm.tvIndexNavn)
                .filter((d) => !d.beregnet)
                .filter((l) => l.x >= options.xMin && l.x <= options.xMax);
            const series = dffHighchartsSetSeries(vm.chart, vm.maaltSerie);
            series.setData(forbrug);

            // Forbrug - Beregnet
            const forbrugBeregnet = this.mapForbrugsLinjer(forbrugsLinjer, vm.tvIndexNavn)
                .filter((d) => d.beregnet)
                .filter((l) => l.x >= options.xMin && l.x <= options.xMax);
            if (forbrugBeregnet.length) {
                const seriesForbrugBeregnet = dffHighchartsSetSeries(vm.chart, vm.beregnetSerie);
                seriesForbrugBeregnet.setData(forbrugBeregnet);
            }

            // Forventet forbrug
            if (aarsmaerke === vm.aarsmaerke) {
                const forventetForbrug = this.mapForventetForbrugsLinjer(resultThisYear, vm.tvIndexNavn);
                dffHighchartsSetSeries(vm.chart, vm.forventetSerie).setData(forventetForbrug);
            }
            return this.forbrugArrayToTaellevaerker([resultThisYear]);
        } catch (error) {
            this.aErrorChart(error, vm.chart);
            throw error;
        } finally {
            vm.chart.hideLoading();
            if (!vm.chart.hasData()) {
                vm.chart.showNoData("Der er desværre ingen data at vise i denne periode");
            }
        }
    }

    private forbrugArrayToTaellevaerker(forbrug: Forbrug[]): string[] {
        return [...new Set(forbrug.reduce((acc, curr) => [...acc, ...curr.taellevaerker], []))].filter((tv) => !!tv);
    }

    private getForbrugChartOptions(data: Forbrug, taellevaerkIndexNavn: string, override: Partial<DffChartOptions> = {}): DffChartOptions {
        const forbrugslinje = data.forbrugsLinjer.find((l) => !l.ekskluderetMedFilter);
        const taellevaerk = forbrugslinje && forbrugslinje.taellevaerker.find((tv) => tv.indexNavn === taellevaerkIndexNavn || tv.indexNavn === "none");
        const enhedsTekstTmp = taellevaerk ? taellevaerk.enhedsTekst : "";
        const enhedsTekst = enhedsTekstTmp === "" && taellevaerkIndexNavn === "Volumen" ? "m³" : enhedsTekstTmp;
        const chartTitle = "";
        const chartSubTitle = taellevaerkIndexNavn === "ENG1" ? "Energi" : `Volumen (${enhedsTekst})`;
        const xMin = resetMonth(data.aar.start).getTime();
        const xMax = resetMonth(data.aar.slut).getTime();
        const yTitle = enhedsTekst;
        const tooltipValueSuffix = " " + enhedsTekst;
        const result: DffChartOptions = {
            chartTitle,
            chartSubTitle,
            xMin,
            xMax,
            yTitle,
            tooltipValueSuffix
        };
        return { ...result, ...override };
    }

    private getTempChartOptions(data: Forbrug, taellevaerkIndexNavn: string, override: Partial<DffChartOptions> = {}): DffChartOptions {
        const enhedsTekst = "°C";
        const chartTitle = "";
        const chartSubTitle = "Temperatur";
        const xMin = resetMonth(data.aar.start).getTime();
        const xMax = resetMonth(data.aar.slut).getTime();
        const yTitle = enhedsTekst;
        const tooltipValueSuffix = " " + enhedsTekst;
        const result: DffChartOptions = {
            chartTitle,
            chartSubTitle,
            xMin,
            xMax,
            yTitle,
            tooltipValueSuffix
        };
        return { ...result, ...override };
    }

    private applyChartOptions(chart: any, options: DffChartOptions, maximizedPanel: boolean): void {
        chart.chartTitle = options.chartTitle.toString();
        chart.chartSubtitle = options.chartSubTitle.toString();
        if (maximizedPanel) {
            chart.setTitle({ text: options.chartTitle }, { text: options.chartSubTitle });
        }
        chart.xAxis[0].update({ min: options.xMin, max: options.xMax, plotBands: [] }, false);
        chart.yAxis[0].update({ title: { text: options.yTitle } }, false);
    }

    /**
     *
     * @param data
     * @param taellevaerkIndexNavn
     * @param shiftYear bruges når data for flere år skal vises sammen.
     * Er aktuelle år eksempelvis 2020, kan man hente data for 2019 med shiftYear=1,
     * hvilket medfører at dataene returneres som var de for 2020.
     */
    private mapForbrugsLinjer(linjer: ForbrugsLinje[], taellevaerkIndexNavn: string, mapDate?: (date: Date) => Date): ForbrugChartPoint[] {
        return linjer.map((l) => this.mapForbrugsLinje(l, taellevaerkIndexNavn, mapDate));
    }

    private mapForbrugsLinje(
        linje: ForbrugsLinje,
        taellevaerkIndexNavn: string,
        mapDate?: (date: Date) => Date,
        mapPeriode?: (periode: Periode) => Periode
    ): ForbrugChartPoint {
        const date = ((mapDate && mapDate(linje.periode.slut)) || linje.periode.slut).getTime();
        const periode = mapPeriode ? mapPeriode(linje.periode) : linje.periode;
        taellevaerkIndexNavn = this.getTaellevaerkIndexNavn(taellevaerkIndexNavn, linje.taellevaerker);
        const taellevaerk = linje.taellevaerker.find((tv) => tv.indexNavn === taellevaerkIndexNavn);
        const beregnet = linje.beregnet;
        return {
            x: date,
            y: taellevaerk && taellevaerk.forbrug,
            periode,
            beregnet,
            valueSuffix: " " + ((taellevaerk && taellevaerk.enhedsTekst) || ""),
            maalerAflaesning: taellevaerk && taellevaerk.aflaesning.slut
        };
    }

    private mapForventetForbrugsLinjer(data: Forbrug, taellevaerkIndexNavn: string, mapDate?: (date: Date) => Date): ForventetForbrugChartPoint[] {
        const result = data.forbrugsLinjer.map((linje) => {
            const date = ((mapDate && mapDate(linje.periode.slut)) || linje.periode.slut).getTime();
            taellevaerkIndexNavn = this.getTaellevaerkIndexNavn(taellevaerkIndexNavn, linje.taellevaerker);
            const taellevaerk = linje.taellevaerker.find((tv) => tv.indexNavn === taellevaerkIndexNavn);
            const forventetForbrug = taellevaerkIndexNavn === "ENG1" ? linje.forventetForbrug.eng1 : linje.forventetForbrug.m3;
            const foreventetAflaesning = taellevaerkIndexNavn === "ENG1" ? linje.forventetAflaesning.eng1 : linje.forventetAflaesning.m3;
            return {
                x: date,
                y: forventetForbrug,
                start: taellevaerk.aflaesning.start,
                maalerAflaesning: foreventetAflaesning,
                valueSuffix: " " + taellevaerk.enhedsTekst
            };
        });
        return result;
    }

    private getTaellevaerkIndexNavn(oensketTaellevaerkIndexNavn: string, taellevaerker: Taellevaerk[]): string {
        // FIX. "taellevaerkIndexNavn" har standardværdien "ENG1", hvis ikke bruger har valgt noget andet.
        // Det virker fint i de fleste situationer, men vandforsyninger har ikke linier med "ENG1",
        // I så fald benytter vi et af de tælleværker som findes i ataene.
        const taellevaerk = taellevaerker.find((tv) => tv.indexNavn === oensketTaellevaerkIndexNavn) || taellevaerker.find((tv) => !!tv.indexNavn);
        return (taellevaerk && taellevaerk.indexNavn) || "";
    }

    private mapTemperaturLinjer<T>(linjer: T[], getLinje: (context: T) => ForbrugsLinje, mapDate?: (context: T) => Date): TemperaturChartPoints {
        const result = linjer
            .map((l) => ({ date: mapDate(l), linje: getLinje(l) }))
            .sort((a, b) => a.date.getTime() - b.date.getTime())
            .map((l) => this.mapTemperaturLinje(l.linje, () => l.date));
        const valideVaerdier = (a: XYChartPoint): boolean => a.y > 0 && a.y < 100;
        return {
            afkoeling: result.map((r) => r.afkoeling).filter(valideVaerdier),
            fremloeb: result.map((r) => this.nulHvisSammeVaerdi(r.fremloeb, r.retur)).filter(valideVaerdier),
            retur: result.map((r) => this.nulHvisSammeVaerdi(r.retur, r.fremloeb)).filter(valideVaerdier),
            forventetRetur: result.map((r) => r.forventetRetur).filter(valideVaerdier),
            neutral: result.map((r) => r.neutral)
        };
    }

    private nulHvisSammeVaerdi(value: XYChartPoint, compare: XYChartPoint): XYChartPoint {
        // Hvis værdien er den samme som sammenligningsværdien, sættes den til 0.
        // Dette betyder at den bliver invalid, hvilket efterfølgende resulterer i at den filtreres fra.
        // Det er lavet i forbindelse med EFOR-601, hvor man ønsker at undlade punkter hvor temperaturen for Fremløb og Retur er den samme.
        return value.x === compare.x && value.y === compare.y ? { x: value.x, y: 0 } : value;
    }

    private mapTemperaturLinje(linje: ForbrugsLinje, mapDate?: (date: Date) => Date): TemperaturChartPoint {
        const dato = linje.periode.slut;
        const x = ((mapDate && mapDate(dato)) || dato).getTime();
        return {
            fremloeb: { x, y: linje.temperatur.frem },
            retur: { x, y: linje.temperatur.retur },
            afkoeling: { x, y: linje.temperatur.afkoeling },
            forventetRetur: { x, y: linje.temperatur.forventeRetur },
            neutral: {
                x,
                low: linje.temperatur.neutraltOmraade.nedre,
                high: linje.temperatur.neutraltOmraade.oevre
            }
        };
    }

    private resetTempChart(vm: MitForbrugViewModel): void {
        for (const series of vm.tempOptions.series) {
            series.data = [];
        }
        vm.tempChart = new Chart(vm.tempOptions);
        vm.tempDrillDownPress = true;
        this.reloadTempChart(vm);
        vm.tempDrillDownPress = false;
    }

    private getAarsmaerkeFraDato(vm: MitForbrugViewModel, dato: string): number {
        const saDato = vm.aarsmaerkeStart.split("-");
        const sParmDato = dato.split("-");

        if (parseInt(sParmDato[1], 10) >= parseInt(saDato[1], 10)) {
            return parseInt(sParmDato[2], 10);
        } else {
            return parseInt(sParmDato[2], 10) - 1;
        }
    }

    private hasCalculatedData(graphData: any): any {
        if (graphData !== null) {
            let notCalculated = false;
            let calculated = false;
            if (graphData !== undefined && graphData.data !== null) {
                graphData = graphData.data;
                for (const i in graphData.ForbrugsLinjer.TForbrugsLinje) {
                    // måneder eller dage
                    if (graphData.ForbrugsLinjer.TForbrugsLinje[i].EkskluderetMedFilter === "False") {
                        if (graphData.ForbrugsLinjer.TForbrugsLinje[i].Fiktiv === "True") {
                            calculated = true;
                        } else if (graphData.ForbrugsLinjer.TForbrugsLinje[i].Fiktiv === "False") {
                            notCalculated = true;
                        }
                    }
                }
                return [calculated, notCalculated];
            }
        }
        return [false, true];
    }

    private getStartMonth(vm: MitForbrugViewModel, startYearDate: string): number {
        const user = vm.aktuelForbruger;
        const x = parseDate(startYearDate, "DD-MM-YYYY");
        const y = user.indflyttet;
        if (x.getDay() > y.getDay()) {
            return x.getMonth() + 1;
        } else {
            return y.getMonth() + 1;
        }
    }

    private getYearList(vm: MitForbrugViewModel): any {
        const list = [];
        if (vm.aarsmaerke > 0) {
            for (let a = this.getIndflyttetAarsmaerke(vm); a <= vm.aarsmaerke; a++) {
                list.unshift({ id: a, value: a });
            }
        }
        return list;
    }

    private getForbrugMinYear(vm: MitForbrugViewModel): number {
        const indflyttetAarsmaerke = this.getIndflyttetAarsmaerke(vm);
        const minAarsmaerke = this.minAarsmaerke(vm.aarsmaerke, vm.umbracoMitForbrug.forbrugsgraf.startAar);
        return Math.max(indflyttetAarsmaerke, minAarsmaerke);
    }

    private getForbrugMinDate(vm: MitForbrugViewModel): Date {
        const minAarsmaerke = this.minAarsmaerke(vm.aarsmaerke, vm.umbracoMitForbrug.forbrugsgraf.startAar);
        const umbracoMinDate = new Date(minAarsmaerke, 0, 1);
        const indflytDate = vm.aktuelForbruger.indflyttet;
        return maxDate(umbracoMinDate, indflytDate);
    }

    private getForbrugMaxDate(): Date {
        return new Date();
    }

    private getTemperaturMinYear(vm: MitForbrugViewModel): number {
        const indflyttetAarsmaerke = this.getIndflyttetAarsmaerke(vm);
        const minAarsmaerke = this.minAarsmaerke(vm.aarsmaerke, vm.umbracoMitForbrug.temperaturgraf.startAar);
        return Math.max(indflyttetAarsmaerke, minAarsmaerke);
    }

    private getTemperaturMinDate(vm: MitForbrugViewModel): Date {
        const minAarsmaerke = this.minAarsmaerke(vm.aarsmaerke, vm.umbracoMitForbrug.temperaturgraf.startAar);
        const umbracoMinDate = new Date(minAarsmaerke, 0, 1);
        const indflytDate = vm.aktuelForbruger.indflyttet;
        return maxDate(umbracoMinDate, indflytDate);
    }

    private getTemperaturMaxDate(): Date {
        return new Date();
    }

    private minAarsmaerke(aarsmaerke: number, umbracoStartAarEllerAntalAarTilbageITiden: number): number {
        // Har man ikke angivet noget, tillader vi at gå langt tilbage i tiden (til år 1900).
        umbracoStartAarEllerAntalAarTilbageITiden = umbracoStartAarEllerAntalAarTilbageITiden || 1900;
        // Hvis man har angivet et 4-cifret tal, går vi ud fra der er tale om et årstal
        // Ellers går vi ud fra man har angivet hvor mange år tilbage i tiden man må gå.
        return umbracoStartAarEllerAntalAarTilbageITiden >= 1000 ? umbracoStartAarEllerAntalAarTilbageITiden : aarsmaerke - umbracoStartAarEllerAntalAarTilbageITiden;
    }

    private getIndflyttetAarsmaerke(vm: MitForbrugViewModel): number {
        const indflyttet = vm.aktuelForbruger.indflyttet;
        const aarsmaerke = parseDate(vm.aarsmaerkeStart, "DD-MM-YYYY");
        return indflyttet.getMonth() < aarsmaerke.getMonth() ? indflyttet.getFullYear() - 1 : indflyttet.getFullYear();
    }

    private httpGetForbrug(params: ForbrugParams): Promise<Forbrug> {
        return this.forbrugService.getForbrug(params).pipe(first()).toPromise();
    }

    private aError(error: any): void {
        console.error(error);
        this.logger.logError(error);
        this.toastService.visFejl("Funktionen er ikke tilgængelig i øjeblikket - Prøv igen senere");
    }

    private aErrorChart(error: any, chart: any): void {
        console.error(error);
        chart.hideLoading();
        this.logger.logError(error);
        this.toastService.visFejl("Funktionen er ikke tilgængelig i øjeblikket - Prøv igen senere");
    }
}

export interface ForbrugChartPoint {
    x: number;
    y: number;
    periode: { start: Date; slut: Date };
    beregnet: boolean;
    maalerAflaesning: number;
    valueSuffix: string;
}

export interface ForventetForbrugChartPoint {
    x: number;
    y: number;
    start: number;
    maalerAflaesning: number;
    valueSuffix: string;
}

export interface TemperaturChartPoints {
    fremloeb: XYChartPoint[];
    retur: XYChartPoint[];
    afkoeling: XYChartPoint[];
    forventetRetur: XYChartPoint[];
    neutral: LowHighChartPoint[];
}

export interface TemperaturChartPoint {
    fremloeb: XYChartPoint;
    retur: XYChartPoint;
    afkoeling: XYChartPoint;
    forventetRetur: XYChartPoint;
    neutral: LowHighChartPoint;
}

export interface XYChartPoint {
    x: number;
    y: number;
}

export interface LowHighChartPoint {
    x: number;
    low: number;
    high: number;
}

export interface DffChartOptions {
    chartTitle: string | number;
    chartSubTitle: string;
    xMin: number;
    xMax: number;
    yTitle: string;
    tooltipValueSuffix: string;
}
